import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHANNELS,
  DEFAULT_CHANNELS,
  EVENT_SEVERITY,
  NOTIFICATION_EVENTS,
  hasCapability,
  type Channel,
  type NotificationEvent,
} from '@prooffleet/contracts';
import { api, useSession } from '@prooffleet/client';
import { severityColor } from '@prooffleet/ui';
import { PageHeader } from '../components/Page.js';
import {
  currentSubscription,
  pushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '../components/pushClient.js';

interface PrefRow {
  event: NotificationEvent;
  channels: Channel[];
  quietHours: { from: string; to: string } | null;
}

interface Coverage {
  totalUsers: number;
  withPush: number;
  withoutPush: { userId: string; roleKey: string }[];
}

const EVENT_LABEL: Record<NotificationEvent, string> = {
  'defect.critical': 'Critical defect reported',
  'defect.reported': 'Defect reported',
  'defect.closed': 'Defect closed',
  'inspection.missed': 'Inspection missed',
  'inspection.submitted': 'Inspection submitted',
  'trust.low': 'Low trust score',
};

const CHANNEL_LABEL: Record<Channel, string> = {
  in_app: 'In app',
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
};

/**
 * Настройки уведомлений и охват push.
 *
 * Тихие часы соблюдаются по уровню: важное ночью не будит, но остаётся
 * в интерфейсе, а критичное их игнорирует — иначе уровень теряет смысл.
 *
 * Диагностика охвата здесь же и не случайно: подписка привязана к устройству,
 * на iOS работает только у установленного на домашний экран приложения, и
 * менеджер должен видеть дыру до происшествия, а не после.
 */
export function NotificationSettings(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canSeeCoverage = actor !== null && hasCapability(actor, 'user:read');
  const queryClient = useQueryClient();

  const [prefs, setPrefs] = useState<Record<string, PrefRow>>({});
  const [pushState, setPushState] = useState<'unknown' | 'on' | 'off' | 'unsupported'>('unknown');
  const [pushError, setPushError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api<{ prefs: PrefRow[] }>('/notification-prefs'),
  });

  const { data: coverage } = useQuery({
    queryKey: ['push-coverage'],
    enabled: canSeeCoverage,
    queryFn: () => api<Coverage>('/push/coverage'),
  });

  useEffect(() => {
    if (data === undefined) return;
    const stored = new Map(data.prefs.map((p) => [p.event, p]));
    const next: Record<string, PrefRow> = {};
    for (const event of NOTIFICATION_EVENTS) {
      // Ненастроенное событие показывается со значениями по умолчанию, а не
      // пустым: иначе выглядит так, будто уведомления выключены.
      next[event] = stored.get(event) ?? {
        event,
        channels: [...DEFAULT_CHANNELS[EVENT_SEVERITY[event]]],
        quietHours: null,
      };
    }
    setPrefs(next);
  }, [data]);

  useEffect(() => {
    if (!pushSupported()) {
      setPushState('unsupported');
      return;
    }
    void currentSubscription().then((sub) => {
      setPushState(sub === null ? 'off' : 'on');
    });
  }, []);

  const save = useMutation({
    mutationFn: (row: PrefRow) =>
      api('/notification-prefs', {
        method: 'PUT',
        body: { event: row.event, channels: row.channels, quietHours: row.quietHours },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-prefs'] });
    },
  });

  const toggle = (event: NotificationEvent, channel: Channel, on: boolean): void => {
    const row = prefs[event];
    if (row === undefined) return;
    const channels = on ? [...row.channels, channel] : row.channels.filter((c) => c !== channel);
    const next = { ...row, channels };
    setPrefs((prev) => ({ ...prev, [event]: next }));
    save.mutate(next);
  };

  const setQuiet = (event: NotificationEvent, from: string, to: string): void => {
    const row = prefs[event];
    if (row === undefined) return;
    const quietHours = from === '' || to === '' ? null : { from, to };
    const next = { ...row, quietHours };
    setPrefs((prev) => ({ ...prev, [event]: next }));
    save.mutate(next);
  };

  return (
    <Stack gap="lg">
      <div>
        <PageHeader
          title="Notification settings"
          hint="Quiet hours apply per level: important alerts wait until morning but stay visible in the app, critical ones ignore them."
        />

        <Table.ScrollContainer minWidth={820}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="30%">Event</Table.Th>
                {CHANNELS.map((channel) => (
                  <Table.Th key={channel} w="11%">
                    {CHANNEL_LABEL[channel]}
                  </Table.Th>
                ))}
                <Table.Th w="26%">Quiet hours</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {NOTIFICATION_EVENTS.map((event) => {
                const row = prefs[event];
                const severity = EVENT_SEVERITY[event];
                return (
                  <Table.Tr key={event}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Badge size="xs" color={severityColor(severity)}>
                          {severity}
                        </Badge>
                        <Text fz="sm">{EVENT_LABEL[event]}</Text>
                      </Group>
                    </Table.Td>
                    {CHANNELS.map((channel) => (
                      <Table.Td key={channel}>
                        <Checkbox
                          checked={row?.channels.includes(channel) ?? false}
                          onChange={(e) => toggle(event, channel, e.currentTarget.checked)}
                        />
                      </Table.Td>
                    ))}
                    <Table.Td>
                      {severity === 'critical' ? (
                        <Text fz="xs" c="dimmed">
                          ignored for critical
                        </Text>
                      ) : (
                        <Group gap={4} wrap="nowrap">
                          <TextInput
                            size="xs"
                            placeholder="22:00"
                            value={row?.quietHours?.from ?? ''}
                            onChange={(e) =>
                              setQuiet(event, e.currentTarget.value, row?.quietHours?.to ?? '')
                            }
                          />
                          <TextInput
                            size="xs"
                            placeholder="07:00"
                            value={row?.quietHours?.to ?? ''}
                            onChange={(e) =>
                              setQuiet(event, row?.quietHours?.from ?? '', e.currentTarget.value)
                            }
                          />
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Card maw={560}>
        <Group justify="space-between" align="center" mb="xs">
          <Text fw={600} fz="sm">
            Push on this device
          </Text>
          <Badge color={pushState === 'on' ? 'gray' : 'red.8'} variant={pushState === 'on' ? 'light' : 'filled'}>
            {pushState === 'on' ? 'active' : pushState === 'unsupported' ? 'unsupported' : 'off'}
          </Badge>
        </Group>
        <Text c="dimmed" fz="sm" mb="sm">
          A subscription belongs to a device, not to an account. Turning it off here does not affect
          your other devices.
        </Text>

        {pushError !== null && (
          <Alert color="red" mb="sm">
            {pushError === 'not_configured'
              ? 'Push is not configured on this server: no VAPID keys. Alerts still appear in the app.'
              : pushError === 'denied'
                ? 'The browser blocked notifications. Allow them in site settings and try again.'
                : 'Could not change the subscription.'}
          </Alert>
        )}

        {pushState !== 'unsupported' && (
          <Button
            variant="default"
            onClick={() => {
              setPushError(null);
              const action = pushState === 'on' ? unsubscribeFromPush : subscribeToPush;
              void action()
                .then(() => setPushState(pushState === 'on' ? 'off' : 'on'))
                .catch((err: unknown) => {
                  setPushError(err instanceof Error ? err.message : 'failed');
                });
            }}
          >
            {pushState === 'on' ? 'Turn off' : 'Turn on'}
          </Button>
        )}
      </Card>

      {canSeeCoverage && coverage !== undefined && (
        <div>
          <Text fw={620} fz="md" mb={2}>
            Push coverage
          </Text>
          <Text c="dimmed" fz="sm" mb="sm" maw="68ch">
            On iOS push only works for an app installed to the home screen. A driver without it never
            sees an alert, and nobody finds out until it matters.
          </Text>
          {coverage.withoutPush.length === 0 ? (
            <Text fz="sm" c="blue.8">
              All {coverage.totalUsers} people have push active.
            </Text>
          ) : (
            <Alert color="orange">
              <Text fz="sm">
                <span className="pf-num">{coverage.withoutPush.length}</span> of{' '}
                <span className="pf-num">{coverage.totalUsers}</span> people have no active push
                subscription:{' '}
                {coverage.withoutPush.map((u) => u.roleKey.replace('_', ' ')).join(', ')}
              </Text>
            </Alert>
          )}
        </div>
      )}
    </Stack>
  );
}
